
import React, { useState, useEffect } from 'react';
import { Button } from "../ui/button";
import { trackEvent } from "@/lib/analytics";
import { CountdownTimer } from "../CountdownTimer";
import { toast } from "sonner";
import { generateResultsVisual } from "../game/GameLogic";
import { generateShareText, shareResults } from "@/lib/shareUtils";
import { getDebugParams, copyDebugLogs } from "@/lib/debugUtils";
import { Bug, Share2, History } from "lucide-react";
import { getStreakRank, getRankDescription } from "@/lib/streakRanks";
import { getEffectiveDate, formatDate } from "@/lib/dateUtils";

interface ResultsDisplayProps {
  effectiveLevelAttempts: number[];
  effectiveGuessHistory: {
    level: number;
    word: string;
    guesses: { letter: string; status: string }[][];
  }[];
  effectiveFailedLevel: number | undefined;
  currentStreak: number;
  previousStreak?: number;
  score: number;
  showDebugLogs?: boolean;
  savedGameStatus: string | null;
  isPracticeMode?: boolean;
  isLivestream?: boolean;
  onToggleGuessHistory?: () => void;
  showGuessHistory?: boolean;
}

export const ResultsDisplay = ({
  effectiveLevelAttempts,
  effectiveGuessHistory,
  effectiveFailedLevel,
  currentStreak,
  previousStreak,
  score,
  showDebugLogs = false,
  savedGameStatus,
  isPracticeMode = false,
  isLivestream = false,
  onToggleGuessHistory,
  showGuessHistory = false
}: ResultsDisplayProps) => {
  const [isInDebugMode, setIsInDebugMode] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [streakRank, setStreakRank] = useState<string | null>(null);
  const [streakRankDescription, setStreakRankDescription] = useState<string | null>(null);
  const [isLostStreak, setIsLostStreak] = useState(false);

  // Practice streaks live in localStorage, so read them directly for practice mode
  const practiceStreak = isPracticeMode ? parseInt(localStorage.getItem('babblegram_practice_streak') || '0', 10) : 0;
  const practicePreviousStreak = isPracticeMode ? parseInt(localStorage.getItem('babblegram_practice_previous_streak') || '0', 10) : 0;

  const effectiveCurrentStreak = isPracticeMode ? practiceStreak : currentStreak;
  const effectivePreviousStreak = isPracticeMode ? practicePreviousStreak : previousStreak;

  useEffect(() => {
    const debugParams = getDebugParams();
    setIsInDebugMode(debugParams.showLogs);

    if (effectiveCurrentStreak > 0) {
      setStreakRank(getStreakRank(effectiveCurrentStreak));
      setStreakRankDescription(getRankDescription(effectiveCurrentStreak));
      setIsLostStreak(false);
    } else if (effectiveCurrentStreak === 0 && effectivePreviousStreak && effectivePreviousStreak > 0) {
      setIsLostStreak(true);
    } else {
      setIsLostStreak(false);
    }
  }, [effectiveCurrentStreak, effectivePreviousStreak]);

  // Debug logging for score calculation
  useEffect(() => {
    console.log("ResultsDisplay mounted with:", {
      effectiveLevelAttempts,
      effectiveFailedLevel,
      effectiveGuessHistory,
      savedGameStatus,
      score,
      effectiveCurrentStreak,
      effectivePreviousStreak,
      isPracticeMode
    });
  }, [effectiveLevelAttempts, effectiveFailedLevel, effectiveGuessHistory, savedGameStatus, score, effectiveCurrentStreak, effectivePreviousStreak, isPracticeMode]);

  const getStreakStatusText = () => {
    if (isLostStreak) {
      return effectivePreviousStreak && effectivePreviousStreak > 0 ?
        `Lost streak of ${effectivePreviousStreak} days (was a "${getStreakRank(effectivePreviousStreak)}")` : '';
    } else if (streakRank && effectiveCurrentStreak > 0) {
      return `Current rank: "${streakRank}" (${effectiveCurrentStreak}-day streak)`;
    }
    return '';
  };

  const handleShare = async () => {
    if (isSharing) return;

    try {
      setIsSharing(true);

      const results = generateResultsVisual(
        effectiveLevelAttempts,
        effectiveGuessHistory,
        effectiveFailedLevel,
        false
      );

      const gameUrl = window.location.origin;
      
      // Use effective date for today (debug-aware). For practice mode, prefer stored practice date if present.
      let today = formatDate(getEffectiveDate());
      if (isPracticeMode) {
        const practiceDate = localStorage.getItem('babblegram_practice_mode_date');
        if (practiceDate) {
          try {
            const parsedDate = new Date(practiceDate);
            today = parsedDate.toISOString().split('T')[0];
            console.log('🔗 SHARE - Using practice mode date for share:', { practiceDate, today });
          } catch (error) {
            console.error('Error parsing practice mode date:', error);
          }
        }
      }

      const safeFailedLevel = effectiveFailedLevel !== undefined &&
        !isNaN(Number(effectiveFailedLevel)) &&
        Number(effectiveFailedLevel) > 0 ?
        Number(effectiveFailedLevel) : undefined;

      const shareText = await generateShareText(
        today,
        results,
        score,
        effectiveCurrentStreak,
        gameUrl,
        effectivePreviousStreak,
        effectiveLevelAttempts,
        safeFailedLevel,
        isPracticeMode
      );

      const success = await shareResults(shareText);

      trackEvent('share_results', {
        mode: isPracticeMode ? 'practice' : 'daily',
        score,
        streak: effectiveCurrentStreak,
        outcome: savedGameStatus ?? 'unknown',
        method: success ? 'shared' : 'failed'
      });

      if (!success) {
        throw new Error("Failed to copy results to clipboard");
      }
    } catch (error) {
      console.error("Error in sharing process:", error);
      toast.error("Unable to share results. Please try again.");
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyLogs = async () => {
    const success = await copyDebugLogs();
    if (!success) {
      toast.error("Failed to copy debug logs");
    }
  };

  const results = generateResultsVisual(
    effectiveLevelAttempts,
    effectiveGuessHistory,
    effectiveFailedLevel,
    true
  );

  return (
    <div className="results-display mb-4 relative" style={{ height: "auto" }}>
      <div
        className={`w-full overflow-hidden rounded-xl shadow-xl dot-pattern ${isPracticeMode ? 'dot-pattern-purple' : ''}`}
        style={{
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        <div className="absolute inset-0 bg-gray-900/50 rounded-xl" />

        <div className="relative z-10 p-4 space-y-3 rounded-xl">
          <div className="space-y-1">
            {results.length > 0 ? (
              results.map((result, index) => (
                <div key={index} className="flex text-gray-300">
                  <span className="w-20 inline-block">{`Level ${index + 1}:`}</span>
                  <span className="inline-block">{result.replace(`Level ${index + 1}: `, '')}</span>
                </div>
              ))
            ) : (
              <div className="text-left whitespace-pre-wrap text-gray-300">
                No results available.
              </div>
            )}
          </div>

          <div className="pt-2">
            <div className="flex justify-between items-center font-bold text-gray-300">
              <span>Score (Lower is Better):</span>
              <span>{score}</span>
            </div>
            {!isPracticeMode && (
              <div className="flex justify-between items-center font-bold mt-1 text-gray-300">
                <span>Next game in:</span>
                <CountdownTimer />
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-col space-y-3">

            {!isLivestream && (
              <Button
                onClick={handleShare}
                className={`w-full ${isPracticeMode ? 'bg-[#9b87f5] hover:bg-[#9b87f5]/90' : 'bg-[#de0046] hover:bg-[#de0046]/90'} text-white`}
                disabled={isSharing}
              >
                <Share2 className="h-4 w-4 mr-1" />
                {isSharing ? "Processing..." : "Share Results"}
              </Button>
            )}

            {onToggleGuessHistory && (
              <Button
                onClick={() => {
                  trackEvent('toggle_guess_history', {
                    action: showGuessHistory ? 'hide' : 'show',
                    mode: isPracticeMode ? 'practice' : 'daily'
                  });
                  onToggleGuessHistory();
                }}
                variant="outline"
                className="w-full text-gray-300 border-gray-700 hover:bg-gray-800 bg-gray-900"
              >
                <History className="h-4 w-4 mr-1" />
                {showGuessHistory ? "Hide Guesses & Answers" : "Show Guesses & Answers"}
              </Button>
            )}

            {showDebugLogs && (
              <Button
                onClick={handleCopyLogs}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                <Bug className="h-4 w-4 mr-1" />
                Copy Debug Logs
              </Button>
            )}
          </div>
        </div>
        <div
          className="pointer-events-none absolute inset-0 z-20 rounded-xl border-[6px] border-solid"
          style={{ borderColor: isPracticeMode ? "#7e22ce" : "#de0046" }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default ResultsDisplay;
